import { GetServerSideProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { unstable_serialize } from 'swr'
import { client } from '~/api/client'
import {
  Schemas,
  getCoordinatesByUserName,
  getMemberByUserName,
  getMembersUserNameSellProductsV1,
} from '~/api/gen'
import { Layout } from '~/components/Layout'
import { Meta } from '~/components/Meta'
import { MemberDetail } from '~/features/MemberDetail/MemberDetail'
import { getMemberDetailJsonLd } from '~/features/MemberDetail/lib/getMemberDetailJsonLd'
import { getMetadata } from '~/features/MemberDetail/lib/getMetadata'
import { buildMemberDlink } from '~/features/shared/memberDetail/lib/buildMemberDlink'
import { getProductsFetchKeyByName } from '~/hooks/useSellProductsByUserName'
import { getPageNumberFromQueryValue } from '~/lib/getPageNumberFromQueryValue'
import { sendGtmEvOnClickDownloadHeader } from '~/lib/sendGtmEvOnClickDownloadHeader'

const GoogleAdScripts = dynamic(
  () => import('~/features/shared/ad/components/GoogleAdScripts'),
  { ssr: false }
)
export type Props = {
  member: Schemas.member_large_detail
  coordinateCollection: Schemas.get_members_user_name_coordinates_v1_response
  pageNumber: number
}

const MemberDetailPage: NextPage<Props> = ({
  member,
  coordinateCollection,
  pageNumber,
}) => {
  const isFirstPage = pageNumber === 1
  const appUrl = buildMemberDlink(member.id)

  return (
    <Layout
      breadcrumbList={[
        member.shop
          ? {
              name: member.shop.name,
              path: `/shop/${member.shop.id}/`,
            }
          : [],
        {
          name: member.nick_name,
          path: `/${member.user_name}/`,
        },
        {
          name: 'コーディネート一覧',
        },
      ].flat()}
      headerProps={{
        headerType: 'border',
        appLink: appUrl,
        hideAppIcon: isFirstPage,
      }}
      appUrl={appUrl}
      appDownloadUrl={appUrl}
      onClickAppLink={sendGtmEvOnClickDownloadHeader}
      abstractUrl="wear.jp/username/"
    >
      <GoogleAdScripts pages={[{ label: 'pc:user' }, { label: 'sp:user' }]} />
      <Meta
        {...getMetadata(member, coordinateCollection)}
        jsonLdList={[getMemberDetailJsonLd(member, coordinateCollection)]}
      />
      <MemberDetail
        member={member}
        coordinateCollection={coordinateCollection}
      />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
}) => {
  const userName = query.username as string
  const { pageNumber, isValid } = getPageNumberFromQueryValue(query.pageno)
  if (!isValid) {
    return {
      redirect: {
        statusCode: 301,
        destination: `/${userName}/`,
      },
    }
  }
  const [memberResponse, coordinateResponse, sellProductsResponse] =
    await Promise.all([
      getMemberByUserName(client, {
        parameter: { user_name: userName },
      }),
      getCoordinatesByUserName(client, {
        parameter: {
          user_name: userName,
          count: '60',
          pageno: pageNumber,
        },
      }),
      getMembersUserNameSellProductsV1(client, {
        parameter: {
          user_name: userName,
          limit: 1,
        },
      }),
    ])
  if (memberResponse.type === 'error') {
    console.error(memberResponse.error)
    if (memberResponse.raw.status === 404) return { notFound: true }
    throw new Error('Failed to get member')
  }
  if (coordinateResponse.type === 'error') {
    console.error(coordinateResponse.error)
    if (coordinateResponse.raw.status === 404) return { notFound: true }
    throw new Error('Failed to get coordinates')
  }

  const sellProducts =
    sellProductsResponse.type === 'success'
      ? sellProductsResponse.data
      : {
          products: [],
          total_count: 0,
          is_y_connect_cooperation: false,
          next_offset: null,
        }

  if (pageNumber > 1 && coordinateResponse.data.coordinates.length === 0) {
    return {
      redirect: {
        statusCode: 301,
        destination: `/${userName}/`,
      },
    }
  }
  return {
    props: {
      fallback: {
        [unstable_serialize(getProductsFetchKeyByName(userName, 1))]:
          sellProducts,
      },
      member: memberResponse.data,
      coordinateCollection: coordinateResponse.data,
      pageNumber,
    },
  }
}

export default MemberDetailPage
